/* =========================================================================
   related-carousel.js
   Arrow + dot controls and per-breakpoint sizing for the product
   recommendation / recently-viewed sliders in sections/related.liquid.

   The card markup is injected at runtime (related-products.js fetches
   /recommendations/products), so this script does not assume any theme
   class names. It finds the scroll container itself, tags it .rc-track,
   tags its children .rc-item, and drives it with native scrolling.

   Override the detection with data-rc-selector on the [data-rc] region
   if the heuristic picks the wrong element.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function scrollBehavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  function isScroller(el) {
    var overflowX = window.getComputedStyle(el).overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  /* Find the element that actually holds the product cards.
     Priority: pinned selector → a scrollable element that overflows →
     any scrollable element → the element with the most children. */
  function findTrack(region) {
    var pinned = region.getAttribute('data-rc-selector');
    if (pinned) {
      try {
        var hit = region.querySelector(pinned);
        if (hit) return hit;
      } catch (e) {
        /* invalid selector entered in the theme editor — fall through */
      }
    }

    var viewport = region.querySelector('[data-rc-viewport]') || region;
    var all = viewport.querySelectorAll('*');
    var scrollers = [];
    var densest = null;
    var densestCount = 1;

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      var count = el.childElementCount;
      if (count < 2) continue;
      if (isScroller(el)) scrollers.push(el);
      if (count > densestCount) {
        densest = el;
        densestCount = count;
      }
    }

    for (var j = 0; j < scrollers.length; j++) {
      if (scrollers[j].scrollWidth > scrollers[j].clientWidth + 1) return scrollers[j];
    }

    return scrollers.length ? scrollers[0] : densest;
  }

  function Carousel(region) {
    this.region = region;
    this.prevBtn = region.querySelector('[data-rc-prev]');
    this.nextBtn = region.querySelector('[data-rc-next]');
    this.dotsEl = region.querySelector('[data-rc-dots]');
    this.dotLabel = region.getAttribute('data-rc-dot-label') || 'Go to slide';

    this.track = null;
    this.items = [];
    this.pages = [0];
    this.index = 0;
    this.ticking = false;

    this._onScroll = this._onScroll.bind(this);
    this._measure = this._measure.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this._step.bind(this, -1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this._step.bind(this, 1));
    }
    region.addEventListener('keydown', this._onKeydown);

    this._watch();
  }

  /* Content arrives async and can be replaced — keep watching. */
  Carousel.prototype._watch = function () {
    var self = this;
    this._connect();

    this.mo = new MutationObserver(function () {
      window.clearTimeout(self._moTimer);
      self._moTimer = window.setTimeout(function () {
        self._connect();
      }, 60);
    });
    this.mo.observe(this.region, { childList: true, subtree: true });
  };

  Carousel.prototype._connect = function () {
    var track = findTrack(this.region);
    if (!track || track.childElementCount < 1) return;

    if (track !== this.track) {
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);

      this.track = track;
      track.classList.add('rc-track');
      track.addEventListener('scroll', this._onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        if (this.ro) this.ro.disconnect();
        /* Also fires when a hidden tab becomes visible (0 → real width). */
        this.ro = new ResizeObserver(this._measure);
        this.ro.observe(track);
      } else {
        window.addEventListener('resize', this._measure);
      }
    }

    var kids = track.children;
    this.items = [];
    for (var i = 0; i < kids.length; i++) {
      kids[i].classList.add('rc-item');
      this.items.push(kids[i]);
    }

    this._measure();
  };

  Carousel.prototype._measure = function () {
    var track = this.track;
    if (!track || !this.items.length) return;
    if (track.clientWidth === 0) return; /* hidden tab — remeasure on resize */

    var overflow = track.scrollWidth - track.clientWidth;
    var scrollable = overflow > 2;

    this.region.setAttribute('data-rc-ready', scrollable ? 'true' : 'false');

    if (!scrollable) {
      this.pages = [0];
      this._renderDots();
      return;
    }

    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
    var itemWidth = this.items[0].getBoundingClientRect().width;
    var perView = Math.max(1, Math.round(track.clientWidth / (itemWidth + gap)));

    var origin = this.items[0].offsetLeft;
    var pages = [];
    for (var i = 0; i < this.items.length; i += perView) {
      pages.push(Math.min(this.items[i].offsetLeft - origin, overflow));
    }

    /* Trailing pages can collapse onto the same max scroll position. */
    this.pages = pages.filter(function (value, idx, arr) {
      return idx === 0 || value !== arr[idx - 1];
    });

    this._renderDots();
    this._update();
  };

  Carousel.prototype._renderDots = function () {
    if (!this.dotsEl) return;

    var needed = this.pages.length > 1 ? this.pages.length : 0;
    if (this.dotsEl.childElementCount === needed) return;

    this.dotsEl.innerHTML = '';
    if (!needed) return;

    for (var i = 0; i < needed; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'rc__dot';
      dot.setAttribute('aria-label', this.dotLabel + ' ' + (i + 1));
      dot.addEventListener('click', this._goTo.bind(this, i));
      this.dotsEl.appendChild(dot);
    }
    this._update();
  };

  Carousel.prototype._step = function (direction) {
    this._goTo(this.index + direction);
  };

  Carousel.prototype._goTo = function (index) {
    if (!this.track) return;
    var target = Math.max(0, Math.min(this.pages.length - 1, index));
    this.track.scrollTo({ left: this.pages[target], behavior: scrollBehavior() });
  };

  Carousel.prototype._onScroll = function () {
    var self = this;
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(function () {
      self.ticking = false;
      self._update();
    });
  };

  Carousel.prototype._update = function () {
    var track = this.track;
    if (!track) return;

    var left = Math.abs(track.scrollLeft);
    var maxScroll = track.scrollWidth - track.clientWidth;

    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < this.pages.length; i++) {
      var distance = Math.abs(this.pages[i] - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    this.index = best;

    if (this.prevBtn) this.prevBtn.disabled = left <= 2;
    if (this.nextBtn) this.nextBtn.disabled = left >= maxScroll - 2;

    if (this.dotsEl) {
      var dots = this.dotsEl.children;
      for (var d = 0; d < dots.length; d++) {
        var active = d === best;
        dots[d].classList.toggle('is-active', active);
        if (active) {
          dots[d].setAttribute('aria-current', 'true');
        } else {
          dots[d].removeAttribute('aria-current');
        }
      }
    }
  };

  Carousel.prototype._onKeydown = function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.track || this.pages.length < 2) return;
    if (!this.region.contains(document.activeElement)) return;
    if (document.activeElement.closest('input, textarea, select')) return;

    event.preventDefault();
    this._step(event.key === 'ArrowRight' ? 1 : -1);
  };

  Carousel.prototype.destroy = function () {
    if (this.mo) this.mo.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._measure);
    this.region.removeEventListener('keydown', this._onKeydown);
    this.region.rcCarousel = null;
  };

  function init(scope) {
    var regions = (scope || document).querySelectorAll('[data-rc]');
    for (var i = 0; i < regions.length; i++) {
      if (regions[i].rcCarousel) continue;
      regions[i].rcCarousel = new Carousel(regions[i]);
    }
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var regions = event.target.querySelectorAll('[data-rc]');
    for (var i = 0; i < regions.length; i++) {
      if (regions[i].rcCarousel) regions[i].rcCarousel.destroy();
    }
  });
})();